import React, { useEffect, useState } from "react";
import { LogOut, Pencil, Save, Mail, ChevronRight } from "lucide-react";
import {
  signUpEmail, signInEmail, signInGoogle, signOut as apiSignOut,
  getSession, onAuthChange, getMyProfile, updateUsername, updateDisplayName,
  fetchOrder,
} from "../lib/api";
import { toast } from "sonner";

function SignInScreen() {
  const [mode, setMode] = useState("choose");
  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const handleGoogle = async () => {
    setBusy(true);
    try { await signInGoogle(); }
    catch (e) { toast.error(e.message || "Google sign-in failed"); setBusy(false); }
  };
  const handleEmailSignIn = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try { await signInEmail({ email: form.email, password: form.password }); toast.success("Welcome back"); }
    catch (e) { toast.error(e.message || "Sign-in failed"); }
    finally { setBusy(false); }
  };
  const handleEmailSignUp = async (e) => {
    e?.preventDefault?.();
    if (!form.email || !form.password) { toast.error("Email and password required"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    setBusy(true);
    try {
      const result = await signUpEmail({ email: form.email, password: form.password, name: form.name });
      // If email confirmation is OFF, a session exists immediately → user is in.
      if (result?.session) {
        toast.success("Account created. Welcome to Million Threads.");
      } else {
        // Try signing in right away (works when confirmation is off but no session returned)
        try {
          await signInEmail({ email: form.email, password: form.password });
          toast.success("Account created. Welcome to Million Threads.");
        } catch {
          toast.success("Account created. You can sign in now.");
          setMode("email-signin");
        }
      }
    } catch (e) { toast.error(e.message || "Sign-up failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="page-first-section relative flex items-center" style={{ minHeight: "calc(80vh - 68px)", paddingBottom: "clamp(2.5rem,6vw,6rem)" }} data-testid="profile-signin">
      <div className="mt-container relative z-10">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-10">
            <p className="font-mono text-[0.7rem] tracking-mt uppercase mb-3" style={{ color: "var(--mt-text-muted)" }}>Member · Access</p>
            <h1 className="font-display font-extralight leading-[1.08] text-[clamp(2.2rem,5vw,3.6rem)]">
              {mode === "email-signup" ? "Join the" : "Welcome"} <span className="italic mt-chrome-text mt-keep-anim" style={{ fontWeight: 600 }}>{mode === "email-signup" ? "Thread." : "Back."}</span>
            </h1>
            <p className="mt-italic-silver mt-3">{mode === "email-signup" ? "Become a Million Threads member." : "Sign in to your account."}</p>
          </div>
          <div className="mt-glass mt-rainbow-edge rounded-[22px] p-7 sm:p-9">
            <div className="space-y-3">
              <button type="button" onClick={handleGoogle} disabled={busy}
                className="w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full transition-all hover:-translate-y-0.5"
                style={{ background: "#fff", color: "#1a1a1a", border: "1px solid var(--mt-glass-border)", fontFamily: "Satoshi, sans-serif", fontWeight: 500, fontSize: "0.85rem" }}
                data-testid="signin-google">
                <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z"/></svg>
                Continue with Google
              </button>
            </div>
            <p className="mt-6 text-center font-mono text-[0.6rem] tracking-mt uppercase leading-relaxed" style={{ color: "var(--mt-text-muted)" }}>
              One tap to join. Your Google account keeps things secure, no passwords to remember.
            </p>
            {/* EMAIL/PASSWORD SIGN-IN — hidden for launch (Google-only). Preserved for future re-enable.
            {mode === "choose" && (
              <button type="button" onClick={() => setMode("email-signin")}
                className="w-full flex items-center justify-center gap-3 px-5 py-3 rounded-full transition-all hover:-translate-y-0.5"
                style={{ background: "var(--mt-text)", color: "var(--mt-bg)", border: "1px solid var(--mt-glass-border)", fontFamily: "Satoshi, sans-serif", fontWeight: 500, fontSize: "0.85rem" }}
                data-testid="signin-email">
                <Mail size={15} /> Continue with Email
              </button>
            )}
            {mode === "email-signin" && (
              <form onSubmit={handleEmailSignIn} className="mt-7 pt-7 border-t" style={{ borderColor: "var(--mt-glass-border)" }}>
                <div className="space-y-3">
                  <input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-input" data-testid="signin-email-input" />
                  <input type="password" placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-input" data-testid="signin-password-input" />
                </div>
                <button type="submit" disabled={busy} className="mt-btn mt-rainbow-edge w-full mt-4" data-testid="signin-submit">{busy ? "..." : "Sign In"} <ChevronRight size={14} /></button>
              </form>
            )}
            {mode === "email-signup" && (
              <form onSubmit={handleEmailSignUp} className="mt-7 pt-7 border-t" style={{ borderColor: "var(--mt-glass-border)" }}>
                <div className="space-y-3">
                  <input type="text" placeholder="Name (optional)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-input" data-testid="signup-name-input" />
                  <input type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-input" data-testid="signup-email-input" />
                  <input type="password" placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-input" data-testid="signup-password-input" />
                  <input type="password" placeholder="Confirm password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-input" data-testid="signup-confirm-input" />
                </div>
                <button type="submit" disabled={busy} className="mt-btn mt-rainbow-edge w-full mt-4" data-testid="signup-submit">{busy ? "..." : "Create Account"} <ChevronRight size={14} /></button>
              </form>
            )}
            <div className="mt-6 pt-6 border-t text-center" style={{ borderColor: "var(--mt-glass-border)" }}>
              <p className="font-mono text-[0.62rem] tracking-mt uppercase mb-3" style={{ color: "var(--mt-text-muted)" }}>{mode === "email-signup" ? "Already have an account?" : "New to Million Threads?"}</p>
              {mode === "email-signup" ? (
                <button onClick={() => setMode("email-signin")} className="font-mono text-[0.7rem] tracking-mt uppercase underline" style={{ color: "var(--mt-text)" }} data-testid="switch-to-signin">Sign in instead</button>
              ) : (
                <button onClick={() => setMode("email-signup")} className="font-mono text-[0.7rem] tracking-mt uppercase underline" style={{ color: "var(--mt-text)" }} data-testid="switch-to-signup">Create an account</button>
              )}
            </div>
            */}
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Profile() {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [editing, setEditing] = useState(false);
  const [usernameInput, setUsernameInput] = useState("");
  const [displayInput, setDisplayInput] = useState("");
  const [myOrders, setMyOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);

  useEffect(() => {
    getSession().then(setSession);
    const unsub = onAuthChange((s) => setSession(s));
    return unsub;
  }, []);

  useEffect(() => {
    if (!session?.user) { setProfile(null); return; }
    getMyProfile().then((p) => { setProfile(p); setUsernameInput(p?.username || ""); setDisplayInput(p?.display_name || ""); });
  }, [session]);

  useEffect(() => {
    if (!session?.user) { setOrdersLoading(false); return undefined; }
    let cancelled = false;
    (async () => {
      try {
        const stored = JSON.parse(window.localStorage.getItem("mt_my_orders") || "[]");
        if (!Array.isArray(stored) || stored.length === 0) { setOrdersLoading(false); return; }
        const results = await Promise.all(stored.map((num) => fetchOrder(num).catch(() => null)));
        if (!cancelled) setMyOrders(results.filter(Boolean));
      } finally { if (!cancelled) setOrdersLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [session]);

  const onSignOut = async () => { await apiSignOut(); setSession(null); setProfile(null); toast.success("Signed out"); };

  const saveProfile = async () => {
    try {
      if (usernameInput.trim() && usernameInput.trim().toLowerCase() !== profile.username) await updateUsername(usernameInput);
      if (displayInput.trim() !== (profile.display_name || "")) await updateDisplayName(displayInput);
      const fresh = await getMyProfile();
      setProfile(fresh); setEditing(false); toast.success("Profile updated");
    } catch (e) { toast.error(e.message || "Could not update profile"); }
  };

  if (session === undefined) {
    return (
      <section className="page-first-section relative" style={{ paddingBottom: "clamp(2.5rem,6vw,6rem)" }}>
        <div className="mt-container relative z-10"><div className="max-w-3xl mx-auto"><div className="mt-glass mt-rainbow-edge rounded-3xl p-10 animate-pulse" style={{ height: 320 }} /></div></div>
      </section>
    );
  }
  if (!session?.user) return <SignInScreen />;

  const displayName = profile?.display_name || profile?.username || "Member";
  const initials = displayName.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <section className="page-first-section relative" style={{ paddingBottom: "clamp(2.5rem,6vw,6rem)" }} data-testid="profile-section">
      <div className="mt-container relative z-10">
        <div className="max-w-3xl mx-auto mt-enter">
          <p className="font-mono text-[0.7rem] tracking-mt uppercase" style={{ color: "var(--mt-text-muted)" }}>Member</p>
          <h1 className="mt-3 font-display font-extralight leading-[1.08] text-[clamp(2.4rem,6vw,4.5rem)]">Your <span className="italic mt-chrome-text mt-keep-anim">Thread.</span></h1>
        </div>
        <div className="max-w-3xl mx-auto mt-12 mt-glass mt-rainbow-edge rounded-3xl p-7 sm:p-10 mt-enter" style={{ transitionDelay: "120ms" }}>
          <div className="flex flex-col sm:flex-row gap-7 items-start sm:items-center">
            <div aria-hidden="true" className="relative w-24 h-24 rounded-full grid place-items-center font-display text-2xl flex-shrink-0"
              style={{ background: "radial-gradient(circle at 35% 30%, #fff, rgba(192,160,255,0.4))", color: "#0a0a0a", border: "1px solid var(--mt-glass-border)", boxShadow: "0 0 24px rgba(0,170,255,0.35)" }} data-testid="profile-avatar">{initials}</div>
            <div className="flex-1 w-full">
              {!editing ? (
                <>
                  <h2 className="font-display text-3xl font-light">{displayName}</h2>
                  <p className="font-mono text-[0.7rem] tracking-mt uppercase opacity-70 mt-1">@{profile?.username || "username"}</p>
                  <p className="font-mono text-[0.62rem] tracking-mt uppercase opacity-50 mt-1">{session.user.email}</p>
                </>
              ) : (
                <div className="space-y-3">
                  <div>
                    <label className="font-mono text-[0.6rem] tracking-mt uppercase block mb-1" style={{ color: "var(--mt-text)" }}>Display Name</label>
                    <input value={displayInput} onChange={(e) => setDisplayInput(e.target.value)} className="mt-input" placeholder="Your name" data-testid="profile-edit-display" />
                  </div>
                  <div>
                    <label className="font-mono text-[0.6rem] tracking-mt uppercase block mb-1" style={{ color: "var(--mt-text)" }}>Username (unique)</label>
                    <input value={usernameInput} onChange={(e) => setUsernameInput(e.target.value.toLowerCase())} className="mt-input" placeholder="username" data-testid="profile-edit-username" />
                    <p className="font-mono text-[0.55rem] tracking-mt opacity-40 mt-1">Letters, numbers, underscores. Min 3 characters.</p>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 self-start">
              {!editing ? (
                <button className="mt-btn" onClick={() => setEditing(true)} data-testid="profile-edit-btn"><Pencil size={14} /> Edit Profile</button>
              ) : (
                <button className="mt-btn mt-rainbow-edge" onClick={saveProfile} data-testid="profile-save-btn"><Save size={14} /> Save</button>
              )}
            </div>
          </div>
          <div className="mt-10">
            <div className="flex items-baseline justify-between mb-4">
              <p className="font-mono text-[0.7rem] tracking-mt uppercase opacity-70">Order History</p>
              {myOrders.length > 0 && (<p className="font-mono text-[0.6rem] tracking-mt uppercase mt-blood" style={{ fontWeight: 700 }} data-testid="profile-orders-real-count">{myOrders.length} {myOrders.length === 1 ? "order" : "orders"}</p>)}
            </div>
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: "var(--mt-glass-border)" }} data-testid="profile-orders">
              <table className="w-full text-left">
                <thead>
                  <tr className="font-mono text-[0.62rem] tracking-mt uppercase opacity-60">
                    <th className="px-4 py-3">Order</th><th className="px-4 py-3">Item</th><th className="px-4 py-3 hidden sm:table-cell">Date</th><th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3 text-right hidden sm:table-cell">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myOrders.map((o) => {
                    const firstItem = o.order_items?.[0]?.product_name || o.items?.[0]?.name || "Item";
                    const more = (o.order_items?.length || o.items?.length || 0) - 1;
                    const itemLabel = more > 0 ? `${firstItem} + ${more} more` : firstItem;
                    return (
                      <tr key={o.order_number} className="border-t" style={{ borderColor: "var(--mt-glass-border)" }} data-testid={`order-row-${o.order_number}`}>
                        <td className="px-4 py-4 font-mono text-[0.7rem]" style={{ color: "var(--mt-text)", fontWeight: 700 }}><span className="inline-block w-1.5 h-1.5 rounded-full mr-2 align-middle" style={{ background: "#00AAFF", boxShadow: "0 0 8px #00AAFF" }} aria-hidden="true" />{o.order_number}</td>
                        <td className="px-4 py-4 font-display text-base">{itemLabel}</td>
                        <td className="px-4 py-4 font-mono text-[0.7rem] opacity-70 hidden sm:table-cell">{(o.created_at || "").slice(0, 10)}</td>
                        <td className="px-4 py-4 font-mono text-sm text-right">{`₹${Number(o.total).toLocaleString("en-IN")}`}</td>
                        <td className="px-4 py-4 font-mono text-[0.65rem] tracking-mt uppercase text-right hidden sm:table-cell" style={{ color: "#00AAFF" }}>{o.status}</td>
                      </tr>
                    );
                  })}
                  {myOrders.length === 0 && !ordersLoading && (<tr><td colSpan={5} className="px-4 py-8 text-center mt-italic-silver">No orders yet. <a href="/shop" className="underline opacity-90 hover:opacity-100">Start your thread.</a></td></tr>)}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-8 text-right">
            <button onClick={onSignOut} className="font-mono text-[0.65rem] tracking-mt uppercase opacity-50 hover:opacity-90 inline-flex items-center gap-2" data-testid="profile-logout"><LogOut size={12} /> Sign Out</button>
          </div>
        </div>
      </div>
    </section>
  );
}
